/**
 * Cognito JWT Service
 * Handles retrieval and validation of AWS Cognito ID Tokens (JWT)
 */

import { clientGraphQLAuthMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

/**
 * Get fresh Cognito ID Token from AWS Cognito
 * Automatically refreshes if expired
 * 
 * @returns {Promise<string>} Cognito ID Token (JWT)
 * @throws {Error} If user not authenticated or token refresh fails
 */
export const getCognitoIdToken = async () => {
  try {
    // Check existing token
    const existingToken = localStorage.getItem('IdToken');
    const refreshToken = localStorage.getItem('RefreshToken');

    if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null' || refreshToken.trim() === '') {
      throw new Error('User not authenticated - missing or invalid refresh token');
    }

    // Validate existing token
    if (existingToken && isTokenValid(existingToken)) {
      console.log('[Cognito] Using existing valid ID token');
      return existingToken;
    }

    // Refresh expired token
    console.log('[Cognito] Token expired or invalid, refreshing...');
    
    const data = await clientGraphQLAuthMutation(
      `mutation refreshToken($refreshToken: String!) {
        authenticationResult: refreshToken(refreshToken: $refreshToken) {
          AccessToken
          IdToken
          RefreshToken
        }
      }`,
      { refreshToken },
      endpoints.GRAPHQL_GENERAL
    );

    const { authenticationResult } = data;
    
    if (!authenticationResult || !authenticationResult.IdToken) {
      throw new Error('Failed to refresh token - no ID token in response');
    }
    
    // Store new tokens
    localStorage.setItem('IdToken', authenticationResult.IdToken);
    localStorage.setItem('AccessToken', authenticationResult.AccessToken);
    
    // Only update RefreshToken if provided (Cognito sometimes returns it, sometimes doesn't)
    if (authenticationResult.RefreshToken) {
      localStorage.setItem('RefreshToken', authenticationResult.RefreshToken);
    }
    
    console.log('[Cognito] Token refreshed successfully');
    return authenticationResult.IdToken;

  } catch (error) {
    console.error('[Cognito] Failed to get ID token:', error);
    throw new Error(`Failed to get Cognito ID token: ${error.message}`);
  }
};

/**
 * Check if JWT token is valid (not expired)
 * Uses 5-minute buffer before actual expiration
 * 
 * @param {string} token - JWT token to validate
 * @returns {boolean} True if token is valid and not expiring soon
 */
const isTokenValid = (token) => {
  try {
    if (!token) return false;

    // Decode JWT payload (second part of JWT)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const buffer = 5 * 60; // 5 minutes buffer before expiration
    
    // Check if token has exp claim and is not expiring within buffer time
    return payload.exp && now < (payload.exp - buffer);
  } catch (error) {
    console.error('[Cognito] Failed to validate token:', error);
    return false;
  }
};
